import type { StateCreator } from 'zustand';

import type {
  PageSidebarActiveContent,
  PageSidebarPreviewedDocument,
  PageSidebarScope,
  PageSidebarSessionState,
} from '../../components/page-sidebar/types';
import { isSameTableSidebarRow } from '../../components/table/sidebar/isActiveSidebarRowInTableItems';
import type { RemoveFunctions } from '../../utils/types';
import type { StateSlices } from '..';

const emptyPageSidebarSession = (): PageSidebarSessionState => ({
  active: null,
  opened: false,
  previewedDocument: null,
});

/**
 * Document a freshly opened sidebar previews before its document panel has
 * resolved a selection: the row's own document for document views, nothing for
 * every other sidebar type.
 */
export function initialPreviewedDocument(
  active: PageSidebarActiveContent
): PageSidebarPreviewedDocument | null {
  if (active.sideBarType !== 'docview') {
    return null;
  }

  const documentId = active.data?.data?.id;
  if (typeof documentId !== 'string' && typeof documentId !== 'number') {
    return null;
  }

  const fileName = active.data?.data?.file_name;
  const id = String(documentId);
  return { id, label: typeof fileName === 'string' ? fileName : id };
}

function pageSidebarSessionKey(
  scope: PageSidebarScope
): 'fullscreenPageSidebar' | 'pageSidebar' {
  return scope === 'fullscreen' ? 'fullscreenPageSidebar' : 'pageSidebar';
}

function getPageSidebarSession(
  state: Pick<StateSlices, 'fullscreenPageSidebar' | 'pageSidebar'>,
  scope: PageSidebarScope
): PageSidebarSessionState {
  return state[pageSidebarSessionKey(scope)];
}

function isSameTableDocumentsRow(
  active: PageSidebarActiveContent | null,
  content: PageSidebarActiveContent
): boolean {
  return (
    active != null &&
    active.widgetId === content.widgetId &&
    isSameTableSidebarRow(active.data, content.data)
  );
}

export type UiSlice = {
  closeAllPageSidebars: () => void;
  closePageSidebar: (scope: PageSidebarScope) => void;
  fullscreenPageSidebar: PageSidebarSessionState;
  isNavigationCollapsed: boolean;
  isPageSidebarPinned: boolean;
  openPageSidebar: (
    scope: PageSidebarScope,
    content: PageSidebarActiveContent
  ) => void;
  updatePageSidebarTableRow: (
    scope: PageSidebarScope,
    content: PageSidebarActiveContent
  ) => void;
  pageSidebar: PageSidebarSessionState;
  pageSidebarWidth: number | null;
  resetUiSlice: () => void;
  setNavigationCollapsed: (isCollapsed: boolean) => void;
  setPageSidebarPreviewedDocument: (
    scope: PageSidebarScope,
    document: PageSidebarPreviewedDocument | null
  ) => void;
  setPageSidebarPinned: (pinned: boolean) => void;
  setPageSidebarWidth: (width: number | null) => void;
};

const initialState: RemoveFunctions<UiSlice> = {
  fullscreenPageSidebar: emptyPageSidebarSession(),
  isNavigationCollapsed: false,
  isPageSidebarPinned: false,
  pageSidebar: emptyPageSidebarSession(),
  pageSidebarWidth: null,
};

export const createUiSlice: StateCreator<StateSlices, [], [], UiSlice> = (
  set
) => ({
  ...initialState,
  closeAllPageSidebars() {
    set({
      fullscreenPageSidebar: emptyPageSidebarSession(),
      pageSidebar: emptyPageSidebarSession(),
    });
  },
  closePageSidebar(scope) {
    const sessionKey = pageSidebarSessionKey(scope);
    set({
      [sessionKey]: emptyPageSidebarSession(),
    });
  },
  openPageSidebar(scope, content) {
    const sessionKey = pageSidebarSessionKey(scope);

    set((state) => {
      const session = getPageSidebarSession(state, scope);

      if (session.opened && isSameTableDocumentsRow(session.active, content)) {
        return {
          [sessionKey]: emptyPageSidebarSession(),
        };
      }

      return {
        [sessionKey]: {
          active: content,
          opened: true,
          previewedDocument: initialPreviewedDocument(content),
        },
      };
    });
  },
  updatePageSidebarTableRow(scope, content) {
    const sessionKey = pageSidebarSessionKey(scope);

    set((state) => {
      const session = getPageSidebarSession(state, scope);

      if (
        !session.opened ||
        !session.active ||
        session.active.widgetId !== content.widgetId ||
        !isSameTableSidebarRow(session.active.data, content.data)
      ) {
        return {};
      }

      return {
        [sessionKey]: {
          ...session,
          active: content,
          opened: true,
        },
      };
    });
  },
  resetUiSlice() {
    set({ ...initialState });
  },
  setNavigationCollapsed(isCollapsed) {
    set({ isNavigationCollapsed: isCollapsed });
  },
  setPageSidebarPreviewedDocument(scope, document) {
    const sessionKey = pageSidebarSessionKey(scope);

    set((state) => {
      const session = getPageSidebarSession(state, scope);

      // A document's label never changes, so the id identifies it fully.
      if (session.previewedDocument?.id === document?.id) {
        return {};
      }

      return {
        [sessionKey]: {
          ...session,
          previewedDocument: document,
        },
      };
    });
  },
  setPageSidebarPinned(pinned) {
    set({ isPageSidebarPinned: pinned });
  },
  setPageSidebarWidth(width) {
    set({ pageSidebarWidth: width });
  },
});

export function selectPageSidebarPreviewedDocument(
  state: Pick<StateSlices, 'fullscreenPageSidebar' | 'pageSidebar'>,
  scope: PageSidebarScope
): PageSidebarPreviewedDocument | null {
  return getPageSidebarSession(state, scope).previewedDocument;
}

function selectPageSidebarRowIdToHighlightFromSession(
  session: PageSidebarSessionState,
  widgetId: string
): string | undefined {
  const { active, opened } = session;
  if (!opened || !active || active.widgetId !== widgetId) {
    return undefined;
  }

  return active.data.rowId;
}

export function selectPageSidebarRowIdToHighlight(
  state: Pick<StateSlices, 'pageSidebar'>,
  widgetId: string
): string | undefined {
  return selectPageSidebarRowIdToHighlightFromSession(
    state.pageSidebar,
    widgetId
  );
}

export function selectFullscreenPageSidebarRowIdToHighlight(
  state: Pick<StateSlices, 'fullscreenPageSidebar'>,
  widgetId: string
): string | undefined {
  return selectPageSidebarRowIdToHighlightFromSession(
    state.fullscreenPageSidebar,
    widgetId
  );
}
